import { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, EnvelopeIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Layout } from '@components';
import { getAdminSettings, updateOrderNotificationEmails } from '@services/admin.service';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const OrderNotificationsSettings: FC = () => {
    const [loading, setLoading] = useState(true);
    const [emails, setEmails] = useState<string[]>([]);
    const [newEmail, setNewEmail] = useState('');
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await getAdminSettings();
                if (cancelled) return;
                setEmails(res.data.orderNotificationEmails || []);
            } catch (err: any) {
                if (!cancelled) setError(err?.message || 'Erreur lors du chargement');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleAdd = useCallback((e: FormEvent) => {
        e.preventDefault();
        setError(null);
        setMessage(null);
        const candidate = newEmail.trim().toLowerCase();
        if (!candidate) return;
        if (!EMAIL_REGEX.test(candidate)) {
            setError('Adresse email invalide');
            return;
        }
        if (emails.includes(candidate)) {
            setError('Cette adresse est déjà dans la liste');
            return;
        }
        setEmails((prev) => [...prev, candidate]);
        setNewEmail('');
    }, [newEmail, emails]);

    const handleRemove = useCallback((email: string) => {
        setMessage(null);
        setEmails((prev) => prev.filter((e) => e !== email));
    }, []);

    const handleSave = useCallback(async () => {
        setSaving(true);
        setError(null);
        setMessage(null);
        try {
            const res = await updateOrderNotificationEmails(emails);
            setEmails(res.data.orderNotificationEmails || []);
            setMessage('Liste enregistrée avec succès');
        } catch (err: any) {
            setError(err?.message || "Erreur lors de l'enregistrement");
        } finally {
            setSaving(false);
        }
    }, [emails]);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <span className="loading loading-spinner loading-lg"></span>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6 max-w-2xl">
                <div className="flex items-center gap-3 mb-1">
                    <EnvelopeIcon className="w-6 h-6 text-primary" />
                    <h1 className="text-2xl font-bold">Notifications commandes</h1>
                </div>
                <p className="text-sm text-base-content/60 mb-6">
                    Ces adresses email recevront une copie de chaque commande validée, avec le récapitulatif et le PDF.
                </p>

                {message && (
                    <div className="alert alert-success mb-4">
                        <CheckCircleIcon className="w-5 h-5" />
                        <span>{message}</span>
                    </div>
                )}

                {error && (
                    <div className="alert alert-error mb-4">
                        <XCircleIcon className="w-5 h-5" />
                        <span>{error}</span>
                    </div>
                )}

                <div className="bg-white rounded-lg border border-base-300 p-6">
                    <form onSubmit={handleAdd} className="flex items-end gap-3 mb-6">
                        <div className="form-control flex-1">
                            <label className="label">
                                <span className="label-text">Ajouter une adresse email</span>
                            </label>
                            <input
                                type="email"
                                placeholder="email@exemple.com"
                                className="input input-bordered w-full"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                            />
                        </div>
                        <button type="submit" className="btn btn-primary gap-2" disabled={!newEmail.trim()}>
                            <PlusIcon className="w-4 h-4" />
                            Ajouter
                        </button>
                    </form>

                    {emails.length === 0 ? (
                        <p className="text-center text-base-content/50 py-8">
                            Aucune adresse configurée. Aucune copie ne sera envoyée côté admin.
                        </p>
                    ) : (
                        <ul className="divide-y divide-base-200">
                            {emails.map((email) => (
                                <li key={email} className="flex items-center justify-between py-3">
                                    <span className="text-sm">{email}</span>
                                    <button
                                        onClick={() => handleRemove(email)}
                                        className="btn btn-ghost btn-xs text-error gap-1"
                                    >
                                        <TrashIcon className="w-4 h-4" />
                                        Retirer
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="flex justify-end mt-6">
                        <button onClick={handleSave} className="btn btn-primary" disabled={saving}>
                            {saving ? <span className="loading loading-spinner loading-sm"></span> : 'Enregistrer'}
                        </button>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
